import { STATUS } from "@/constants/statuses";
import CanLeaveReviewSchema from "@apps/reviews/widgets/review_form_modal_widget/schemas/CanLeaveReviewSchema";
import { applyTransaction } from "@datorama/akita";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { Infer, create } from "superstruct";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import {
    reviewsApplicationLoggerToken,
    reviewsAuthApiClientToken,
} from "../../../tokens";
import CanLeaveReviewStore from "../stores/CanLeaveReviewStore";

type CanLeaveReviewRequestParamsDTO = {
    productId?: number;
    reviewRequestToken?: string;
};

@singleton()
class CanLeaveReviewService {
    constructor(
        private canLeaveReviewStore: CanLeaveReviewStore,
        @inject(reviewsAuthApiClientToken)
        private readonly authApiClient: ApiClientInterface,
        @inject(reviewsApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    checkIsCanLeaveReview(
        reqParams?: CanLeaveReviewRequestParamsDTO
    ): Observable<Infer<typeof CanLeaveReviewSchema>> {
        this.logger.debug("CanLeaveReviewService.canLeaveReview is started");
        this.canLeaveReviewStore.setCanLeaveReviewStatus(STATUS.LOADING);

        return this.authApiClient
            .get({
                url: "/review/canLeaveReview",
                responseType: "json",
                queryParams: reqParams,
            })
            .pipe(
                map((v) => create(v.body, CanLeaveReviewSchema)),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "CanLeaveReviewService.canLeaveReview is errored",
                        error
                    );
                    this.canLeaveReviewStore.setCanLeaveReviewStatus(
                        STATUS.ERROR
                    );
                    throw error;
                }),
                tap((v) => {
                    this.logger.debug(
                        "CanLeaveReviewService.canLeaveReview is ready"
                    );
                    applyTransaction(() => {
                        this.canLeaveReviewStore.update(() => ({
                            canLeaveReview: v.canLeaveReview,
                            canLeaveReviewMessageKey: v.messageKey,
                        }));
                        this.canLeaveReviewStore.setCanLeaveReviewStatus(
                            STATUS.READY
                        );
                    });
                })
            );
    }

    resetCanLeaveReviewStore() {
        this.canLeaveReviewStore.reset();
    }
}

export default CanLeaveReviewService;
